import {
  Box,
  Button,
  Card,
  CardContent,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type DocDetail, type DocStatus, type DocType } from "@xop/dashboard-shared";
import type { Dispatch, JSX, ReactNode, SetStateAction } from "react";
import { Link as RouterLink } from "react-router-dom";

import { BackButton } from "@/components/ui/back-button";
import { MultiSelect } from "@/components/ui/multi-select";
import { DOC_TYPES, type FormState } from "./formState.js";
import { folderOptions } from "../folderOptions.js";
import { FolderSelect } from "../FolderSelect.js";

/** The sticky left-hand "details" card of the document form: title, type,
 * category, solution area, release, status, the inline error, and the
 * Cancel / Save actions. Pure presentational — the parent owns `form`. */
export function DocDetailsForm({
  form,
  setForm,
  categories,
  solutionAreas,
  releases,
  isEdit,
  existing,
  error,
  saving,
  children,
}: {
  form: FormState;
  setForm: Dispatch<SetStateAction<FormState>>;
  categories: Array<{ id: number; name: string; parent_id?: number | null }>;
  solutionAreas: Array<{ id: number; name: string }>;
  releases: Array<{ id: number; version: string; name?: string | null }>;
  isEdit: boolean;
  existing: DocDetail | undefined;
  error: string | null;
  saving: boolean;
  /** Slot below the fields and above the actions. Carries the access panel
   * ("who this document is for"), which belongs beside category and release
   * rather than in a tab a busy author never opens — a document nobody
   * scoped is a document every customer can read. */
  children?: ReactNode;
}): JSX.Element {
  return (
    <Card sx={{ alignSelf: "start", position: { lg: "sticky" }, top: { lg: 16 } }}>
      <CardContent sx={{ display: "grid", gap: 2 }}>
        <Box component="header">
          <BackButton to="/documentation" label="Manage" sx={{ mb: 1.5 }} />
          <Typography component="h2" sx={{ mt: 0.5, fontSize: "1.125rem", fontWeight: 600, letterSpacing: "-0.01em" }}>
            {isEdit ? "Edit document" : "New document"}
          </Typography>
          {isEdit && existing && (
            <Typography sx={{ mt: 0.5, fontSize: "11px", color: "text.disabled" }}>
              v{existing.current_version?.version_number ?? "—"} ·{" "}
              {existing.versions_count} version
              {existing.versions_count === 1 ? "" : "s"}
            </Typography>
          )}
        </Box>

        <TextField
          id="doc-title"
          label="Title"
          inputProps={{ required: true }}
          value={form.title}
          onChange={(e) => setForm((s) => ({ ...s, title: e.target.value }))}
          fullWidth
        />

        <Box sx={{ display: "grid", gap: 0.75 }}>
          <TextField
            id="doc-type"
            label="Type"
            select
            value={form.doc_type}
            onChange={(e) => setForm((s) => ({ ...s, doc_type: e.target.value as DocType }))}
            fullWidth
          >
            {DOC_TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                {t.label}
              </MenuItem>
            ))}
          </TextField>
          <Typography sx={{ fontSize: "11px", color: "text.disabled" }}>
            {DOC_TYPES.find((t) => t.value === form.doc_type)?.hint}
          </Typography>
        </Box>

        <FolderSelect
          folders={folderOptions(categories)}
          value={form.document_category_id}
          onChange={(v) => setForm((s) => ({ ...s, document_category_id: v }))}
        />

        {/* Solution areas (product lines) — XOP's go-to-market pillars,
            now a MANAGED multi-select: a doc can cover SEVERAL. Orthogonal
            to the type folder above. Drives the doc-list "Solution"
            switcher; the list itself is managed from the switcher's
            "Manage" link. */}
        <Box sx={{ display: "grid", gap: 0.75 }} data-testid="doc-solution-areas">
          <Typography component="label" sx={{ fontSize: 12, fontWeight: 500, color: "text.secondary" }}>
            Solution areas
          </Typography>
          <MultiSelect
            options={solutionAreas.map((s) => ({ label: s.name, value: String(s.id) }))}
            selected={form.solution_area_ids.map(String)}
            onChange={(next) =>
              setForm((s) => ({ ...s, solution_area_ids: next.map(Number) }))
            }
            placeholder="Add solution areas…"
            emptyMessage="No solution areas yet — add them from Manage."
          />
          <Typography sx={{ fontSize: "11px", color: "text.disabled" }}>
            Which product line(s) this document covers. Used by the “switch
            solution” control on the document list.
          </Typography>
        </Box>

        <TextField
          label="Release"
          select
          value={form.default_release_id}
          onChange={(e) => setForm((s) => ({ ...s, default_release_id: e.target.value }))}
          fullWidth
        >
          <MenuItem value="">None</MenuItem>
          {releases.map((r) => (
            <MenuItem key={r.id} value={String(r.id)}>
              v{r.version}
              {r.name ? ` — ${r.name}` : ""}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label="Status"
          select
          value={form.status}
          onChange={(e) => setForm((s) => ({ ...s, status: e.target.value as DocStatus }))}
          fullWidth
        >
          <MenuItem value="draft">Draft</MenuItem>
          <MenuItem value="in_review">In review</MenuItem>
          <MenuItem value="published">Published</MenuItem>
        </TextField>

        {children}

        {error && (
          <Typography sx={{ borderRadius: 1, bgcolor: (t) => alpha(t.palette.error.main, 0.1), px: 1.5, py: 1, fontSize: 12, color: "error.main" }}>
            {error}
          </Typography>
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, borderTop: 1, borderColor: "divider", pt: 2 }}>
          <Button component={RouterLink} to="/documentation" type="button" variant="text" color="inherit" size="small">
            Cancel
          </Button>
          <Button type="submit" variant="contained" size="small" disabled={saving}>
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create"}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
